import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader } from '../components/ui/PageHeader'
import { leadKeyFor } from '../data/unifiedLeads'
import { fetchAssignmentSnapshot, type LeadAssignment, type StaffMember } from '../features/leads/leadCrmClient'
import { LeadDetailModal } from '../features/leads/LeadDetailModal'
import { LeadFilterPanel } from '../features/leads/LeadFilterPanel'
import {
  activeChips,
  EMPTY_FILTERS,
  filterOptions,
  matchesFilters,
  sortLeads,
  type FilterChip,
  type LeadFilterState,
} from '../features/leads/leadFilterModel'
import { LeadFilters } from '../features/leads/LeadFilters'
import { LeadsSkeleton } from '../features/leads/LeadsSkeleton'
import { LeadTable } from '../features/leads/LeadTable'
import { matchesSearch } from '../features/leads/leadSearch'
import type { Lead } from '../types/crm'
import type { UnifiedLead } from '../types/unifiedLead'

const PAGE_SIZE = 20
const FILTERS_KEY = 'wolf-forge-leads-filters'

// Filters survive leaving and returning to Leads within the browser session.
function readSavedFilters(): LeadFilterState {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(FILTERS_KEY) ?? 'null')
    return saved && typeof saved === 'object' ? { ...EMPTY_FILTERS, ...saved } : EMPTY_FILTERS
  } catch {
    return EMPTY_FILTERS
  }
}

/**
 * The route paints its skeleton first; the working view (search index,
 * filter options and ordering over every lead) renders right after, so
 * navigation never waits on it.
 */
export function LeadsPage({ leads }: { leads: UnifiedLead[] }) {
  const ready = useDeferredValue<UnifiedLead[] | null>(leads, null)
  return ready ? <LeadsWorkspace leads={ready} /> : <LeadsSkeleton />
}

function LeadsWorkspace({ leads: loadedLeads }: { leads: UnifiedLead[] }) {
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Organization edits saved to the sheet this session, shown until the next
  // load reads them back from the sheet itself.
  const [edited, setEdited] = useState<Record<string, UnifiedLead>>({})
  const leads = useMemo(() => loadedLeads.map((lead) => edited[lead.id] ?? lead), [loadedLeads, edited])
  const selectedLead = loadedLeads.find((lead) => lead.id === selectedId) ?? null
  // CRM assignments for every lead in one request; null while loading or if
  // the CRM is unavailable, in which case the Sheet rows still render.
  const [assignments, setAssignments] = useState<Record<string, LeadAssignment> | null>(null)
  const [staff, setStaff] = useState<StaffMember[]>([])
  const keyById = useMemo(() => new Map(loadedLeads.map((lead) => [lead.id, leadKeyFor(lead)])), [loadedLeads])
  const [filters, setFilters] = useState<LeadFilterState>(readSavedFilters)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filterButtonRef = useRef<HTMLButtonElement>(null)

  const loadAssignments = useCallback((signal?: AbortSignal) => {
    // A failed refresh keeps the last known assignments.
    fetchAssignmentSnapshot(signal).then((snapshot) => {
      setAssignments(snapshot.assignments)
      setStaff(snapshot.staff)
    }, () => {})
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    loadAssignments(controller.signal)
    return () => controller.abort()
  }, [loadAssignments])

  useEffect(() => {
    try {
      window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters))
    } catch {
      // Storage can be unavailable; filters then last for this visit only.
    }
  }, [filters])

  const assignmentOf = useCallback((lead: UnifiedLead) => {
    if (!assignments) return null
    const key = keyById.get(lead.id)
    const assignment = key ? assignments[key] : undefined
    return assignment ? `Assigned: ${assignment.assignedStaffName ?? 'Unknown salesperson'}` : 'Unassigned'
  }, [assignments, keyById])
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const selectedLeadTriggerRef = useRef<HTMLElement | null>(null)

  const lookup = useMemo(
    () => ({ assignments, keyOf: (lead: Lead) => keyById.get(lead.id) ?? '' }),
    [assignments, keyById],
  )
  const searched = useMemo(() => leads.filter((lead) => matchesSearch(lead, query)), [leads, query])
  const filteredLeads = useMemo(
    () => sortLeads(searched.filter((lead) => matchesFilters(lead, filters, lookup)), filters.nameOrder),
    [searched, filters, lookup],
  )
  const visibleLeads = filteredLeads.slice(0, visibleCount)
  const options = useMemo(() => filterOptions(leads), [leads])
  const chips = activeChips(filters, staff)
  // Active salespeople, plus inactive ones who still own leads.
  const filterStaff = useMemo(() => {
    const owners = new Set(Object.values(assignments ?? {}).map((entry) => entry.assignedStaffId))
    return staff.filter((member) => member.active || owners.has(member.id))
  }, [staff, assignments])

  function changeQuery(value: string) {
    setQuery(value)
    setVisibleCount(PAGE_SIZE)
  }

  function applyFilters(next: LeadFilterState) {
    setFilters(next)
    setVisibleCount(PAGE_SIZE)
  }

  const closeFilters = useCallback(() => {
    setFiltersOpen(false)
    window.requestAnimationFrame(() => filterButtonRef.current?.focus({ preventScroll: true }))
  }, [])

  function selectLead(lead: UnifiedLead, trigger: HTMLElement) {
    selectedLeadTriggerRef.current = trigger
    setSelectedId(lead.id)
  }

  const closeLead = useCallback(() => {
    setSelectedId(null)
    // The lead may have been (re)assigned in the detail view.
    loadAssignments()
    window.setTimeout(() => selectedLeadTriggerRef.current?.focus(), 0)
  }, [loadAssignments])

  // Name order only reorders, so it alone does not make the list "filtered".
  const filtered = chips.some((chip) => chip.id !== 'order')

  return (
    <div className="page leads-page">
      <PageHeader
        title="Leads"
        description="Companies and their associated owners, combined in one working view."
      />
      <LeadFilters
        chips={chips}
        onClearAll={() => applyFilters(EMPTY_FILTERS)}
        onOpenFilters={() => setFiltersOpen(true)}
        onQueryChange={changeQuery}
        onRemoveChip={(chip: FilterChip) => applyFilters(chip.clear(filters))}
        query={query}
        ref={filterButtonRef}
      />
      <div className="results-summary" aria-live="polite">
        <strong>{filteredLeads.length.toLocaleString()}</strong>{' '}
        {filtered
          ? `of ${searched.length.toLocaleString()} ${searched.length === 1 ? 'lead' : 'leads'} match these filters`
          : `${filteredLeads.length === 1 ? 'lead' : 'leads'} shown from the live sheet`}
      </div>
      {visibleLeads.length > 0 ? (
        <LeadTable assignmentOf={assignmentOf} leads={visibleLeads} selectedId={selectedLead?.id} onSelect={selectLead} />
      ) : (
        <div className="empty-results">
          <h2>{filtered ? 'No leads match these filters' : 'No leads match this search'}</h2>
          {filtered ? (
            <p>
              Loosen a filter or{' '}
              <button className="empty-results-link" onClick={() => applyFilters(EMPTY_FILTERS)} type="button">clear all filters</button>.
            </p>
          ) : (
            <p>Try another company, owner, email, or phone.</p>
          )}
        </div>
      )}
      {visibleCount < filteredLeads.length ? (
        <button className="button button-secondary load-more" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)} type="button">
          Show more leads
        </button>
      ) : null}
      {filtersOpen ? (
        <LeadFilterPanel
          applied={filters}
          assignmentsReady={assignments !== null}
          countFor={(draft) => searched.filter((lead) => matchesFilters(lead, draft, lookup)).length}
          onApply={applyFilters}
          onClose={closeFilters}
          options={options}
          staff={filterStaff}
        />
      ) : null}
      {selectedLead ? (
        <LeadDetailModal
          edited={edited[selectedLead.id]}
          lead={selectedLead}
          onClose={closeLead}
          onLeadUpdated={(lead: Lead) => setEdited((current) => ({ ...current, [selectedLead.id]: lead as UnifiedLead }))}
        />
      ) : null}
    </div>
  )
}
